import { replaceInvalidLinks } from '@lib/replaceInvalidLinks'
import invariant from 'tiny-invariant'
import {
  AllContentDocument,
  AllContentQuery,
  AllContentQueryVariables,
  CheckoutConfirmationPageDocument,
  CheckoutConfirmationPageQuery,
  CheckoutConfirmationPageQueryVariables,
  CheckoutPageDocument,
  CheckoutPageQuery,
  CheckoutPageQueryVariables,
  GlobalCmsDataDocument,
  GlobalCmsDataQuery,
  GlobalCmsDataQueryVariables,
  PageBySlugDocument,
  PageBySlugQuery,
  PageBySlugQueryVariables,
  RedirectEngineDocument,
  RedirectEngineQuery,
  RedirectEngineQueryVariables,
  RedirectLinkFragment,
  Retailer,
  RetailersDocument,
  RetailersQuery,
  RetailersQueryVariables,
  SitemapPagesDocument,
  SitemapPagesQuery,
  SitemapPagesQueryVariables,
  StoriesPageDocument,
  StoriesPageQuery,
  StoriesPageQueryVariables,
  StoriesPostFragment,
  TypedDocumentString,
  UmbracoProductDetailDocument,
  UmbracoProductDetailQuery,
  UmbracoProductDetailQueryVariables,
} from './generated/types.generated'

const endpoint = 'https://graphql.umbraco.io/'

type GraphQlError = {
  message: string
}

type GraphQlErrorRespone<T> = { data: T } | { errors: readonly GraphQlError[] }

export async function umbracoFetch<TData, TVariables>({
  cache = 'no-cache',
  headers,
  query,
  tags,
  variables,
  signal,
}: {
  cache?: RequestCache
  headers?: HeadersInit
  query: TypedDocumentString<TData, TVariables>
  tags?: string[]
  variables?: TVariables
  signal?: AbortSignal | null
}): Promise<TData> {
  const projectAlias = process.env.NEXT_PUBLIC_UMBRACO_PROJECT_ALIAS
  invariant(projectAlias, `Missing NEXT_PUBLIC_UMBRACO_PROJECT_ALIAS!`)

  try {
    const result = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'umb-project-alias': projectAlias,
        ...headers,
      },
      body: JSON.stringify({
        ...(query && { query }),
        ...(variables && { variables }),
      }),
      cache,
      ...(tags && { next: { tags } }),
      signal: signal,
    })

    const body = (await result.json()) as GraphQlErrorRespone<TData>

    if ('errors' in body) {
      throw body.errors[0]
    }
    return body.data
  } catch (e) {
    throw {
      error: e,
      query,
    }
  }
}

export const getRetailers = async (): Promise<
  Retailer[] | null | undefined
> => {
  const res = await umbracoFetch<RetailersQuery, RetailersQueryVariables>({
    query: RetailersDocument,
    variables: {
      where: {
        partnerCategory_starts_with: 'A',
      },
    },
  })

  return res.allRetailer.items as Retailer[]
}

export const getUmbracoProductDetail = async (id: string) => {
  try {
    const res = await umbracoFetch<
      UmbracoProductDetailQuery,
      UmbracoProductDetailQueryVariables
    >({
      query: UmbracoProductDetailDocument,
      variables: {
        where: {
          sublimeCommerceProduct: id,
        },
      },
    })
    return res.allProduct.edges[0].node
  } catch {
    return null
  }
}

export const getPageBySlug = async (url: string, culture?: string) => {
  const res = await umbracoFetch<PageBySlugQuery, PageBySlugQueryVariables>({
    query: PageBySlugDocument,
    variables: { url, culture },
  })

  let page = res.page

  if (!page) {
    page = res.pageFallback
  }

  if (res.page && res.pageFallback && culture != 'en') {
    const allContentRes = await umbracoFetch<
      AllContentQuery,
      AllContentQueryVariables
    >({
      query: AllContentDocument,
      variables: {
        culture: 'en',
      },
    })
    replaceInvalidLinks(page, allContentRes.allContent as any)
  }

  return page
}

export async function getRedirectLinks(): Promise<
  RedirectLinkFragment[] | null | undefined
> {
  const res = await umbracoFetch<
    RedirectEngineQuery,
    RedirectEngineQueryVariables
  >({
    query: RedirectEngineDocument,
  })

  return res.allRedirectEngine.items[0].children.items as any
}

export const getGlobalData = async (culture?: string) => {
  const res = await umbracoFetch<
    GlobalCmsDataQuery,
    GlobalCmsDataQueryVariables
  >({
    query: GlobalCmsDataDocument,
    variables: {
      culture: culture,
    },
  })
  return res
}

export const getSitemapPages = async () => {
  const res = await umbracoFetch<SitemapPagesQuery, SitemapPagesQueryVariables>(
    {
      query: SitemapPagesDocument,
      variables: {
        where: {
          AND: [
            {
              NOT: [{ url_starts_with: '/product-pages' }],
            },
            {
              NOT: [{ url_starts_with: '/test' }],
            },
            { hideFromSitemap: false },
          ],
        },
      },
    },
  )
  return res.allPage.items
}

export const getCheckoutPage = async (culture?: string) => {
  const res = await umbracoFetch<CheckoutPageQuery, CheckoutPageQueryVariables>(
    {
      query: CheckoutPageDocument,
      variables: {
        culture,
      },
    },
  )

  return res
}

export const getConfirmationPage = async () => {
  const res = await umbracoFetch<
    CheckoutConfirmationPageQuery,
    CheckoutConfirmationPageQueryVariables
  >({
    query: CheckoutConfirmationPageDocument,
  })
  return res
}

export async function canAccessRestrictedPage(id: string, token?: string) {
  if (token === 'skip') return true
  const response = await fetch(`https://cdn.umbraco.io/content/${id}`, {
    method: 'GET',
    headers: {
      'Umb-Project-Alias': process.env.UMBRACO_PROJECT_ALIAS!,
      authorization: `Bearer ${token}`,
    },
  })

  return response.status == 200
}

export const retrieveLatestStories = async (culture: string, tags: []) => {
  try {
    const res = await umbracoFetch<StoriesPageQuery, StoriesPageQueryVariables>(
      {
        query: StoriesPageDocument,
        variables: {
          culture: culture,
        },
      },
    )

    const storiesPostGrid = res.page.components.find(
      (component) => component.__typename === 'StoriesPostGrid',
    )
    invariant(
      storiesPostGrid?.__typename === 'StoriesPostGrid',
      'No StoriesPostGrid component found in the response',
    )

    if (storiesPostGrid.blogPosts) {
      return storiesPostGrid.blogPosts
        .filter((blogPost: StoriesPostFragment) => {
          return tags
            ? tags.some((tag) => blogPost.category.includes(tag))
            : true
        })
        .slice(0, 3)
        .map((blogPost: StoriesPostFragment) => ({
          image: blogPost.image,
          headline: blogPost.storiesTitle,
          bodyText: blogPost.storiesTeaser,
          link: blogPost.linkTo,
        }))
    } else {
      console.error('No storiesPostGrid or blogPosts found in the response')
      return []
    }
  } catch (error) {
    console.error('Error fetching blog posts:', error)
    return []
  }
}
